/**
 * GA4 계측. 측정 ID 는 빌드 시 `NEXT_PUBLIC_GA_ID` 로 주입된다(Amplify main 브랜치만 — dev 는 비워 두어 GA 를 붙이지 않는다).
 *
 * 검색 유입 키워드: 구글은 referrer 에서 검색어를 빼고 보내므로 Search Console 연동(GA4 관리 → Search Console 링크)으로 본다.
 * 네이버·다음·빙은 referrer 에 검색어가 남아 있어 여기서 뽑아 `search_engine` / `search_keyword` 이벤트 파라미터로 보낸다.
 * GA4 쪽에서 같은 이름의 맞춤 측정기준(이벤트 범위)을 등록해야 보고서에 나온다.
 */

export const GA_ID = process.env.NEXT_PUBLIC_GA_ID ?? "";

export interface SearchReferrer {
  engine: "naver" | "daum" | "google" | "bing" | "yahoo";
  /** 검색어. 검색엔진이 referrer 에서 검색어를 뺀 경우 null */
  keyword: string | null;
}

/** 호스트명 접미사 → 엔진, 검색어 파라미터 이름 */
const ENGINES: Array<{ suffix: string; engine: SearchReferrer["engine"]; params: string[] }> = [
  { suffix: "search.naver.com", engine: "naver", params: ["query"] },
  { suffix: "search.daum.net", engine: "daum", params: ["q"] },
  { suffix: "bing.com", engine: "bing", params: ["q"] },
  { suffix: "search.yahoo.com", engine: "yahoo", params: ["p"] },
  { suffix: "google.", engine: "google", params: ["q"] },
];

/**
 * @param referrer `document.referrer`
 * @returns 검색엔진 유입이면 엔진·검색어, 아니면 null
 */
export function extractSearchKeyword(referrer: string): SearchReferrer | null {
  if (!referrer) return null;
  let url: URL;
  try {
    url = new URL(referrer);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase();
  const match = ENGINES.find(({ suffix }) =>
    suffix.endsWith(".") ? host.includes(suffix) : host === suffix || host.endsWith(`.${suffix}`) || host.endsWith(suffix)
  );
  if (!match) return null;
  for (const p of match.params) {
    const v = url.searchParams.get(p);
    if (v && v.trim()) return { engine: match.engine, keyword: v.trim() };
  }
  return { engine: match.engine, keyword: null };
}

type Gtag = (...args: unknown[]) => void;

function gtag(): Gtag | null {
  if (typeof window === "undefined") return null;
  const g = (window as unknown as { gtag?: Gtag }).gtag;
  return typeof g === "function" ? g : null;
}

/** 검색 유입 파라미터. 세션 첫 페이지뷰에만 의미가 있지만 referrer 가 남아 있는 동안은 매번 붙여도 무해하다. */
function searchParams(): Record<string, string> {
  if (typeof document === "undefined") return {};
  const s = extractSearchKeyword(document.referrer);
  if (!s) return {};
  const out: Record<string, string> = { search_engine: s.engine };
  if (s.keyword) out.search_keyword = s.keyword;
  return out;
}

/**
 * 페이지뷰 전송. `_app.tsx` 가 첫 렌더와 라우트 전환마다 부른다(gtag config 는 `send_page_view: false`).
 * @param path 현재 경로(`router.asPath`)
 */
export function trackPageView(path: string): void {
  const g = gtag();
  if (!g) return;
  g("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
    ...searchParams(),
  });
}

/**
 * 커스텀 이벤트 전송. gtag 가 없으면(GA 미주입·차단) 조용히 무시한다.
 * @param name GA4 이벤트 이름(권장 이벤트면 그 이름 그대로: generate_lead 등)
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  const g = gtag();
  if (!g) return;
  g("event", name, { ...searchParams(), ...params });
}
